import { useLayoutEffect, useRef } from 'react';
import { allocateStatusRows } from './pedidosBoardLayout';

// Geometry only: no order mutations, persistence or extra polling. Measurements
// include wrapped Salão/Shipping details and any operational errors above the board.
export const usePedidosBoardLayout = (dragging) => {
  const boardRef = useRef(null);
  const wasDraggingRef = useRef(false);

  useLayoutEffect(() => {
    const board = boardRef.current;
    if (!board) return undefined;
    const stack = board.querySelector('.pedidos-grid-column--stacked');
    let frame;
    const measure = () => {
      if (window.innerWidth <= 1200) {
        board.style.removeProperty('--pedidos-board-height');
        stack.style.removeProperty('grid-template-rows');
        return;
      }
      const available = Math.max(240, window.innerHeight - (
        board.getBoundingClientRect().top + window.scrollY
      ) - 6);
      const gap = parseFloat(window.getComputedStyle(stack).rowGap) || 6;
      const rows = Array.from(stack.children).map((slot) => {
        const panel = slot.querySelector('.pedidos-card');
        const list = slot.querySelector('.pedidos-order-list, .pedidos-finished-list');
        const cards = list ? Array.from(list.children).filter((card) => (
          card.matches('.pedidos-order-card, .pedidos-finished-card')
        )) : [];
        // Keep empty status lanes compact while leaving their destinations visible.
        if (!cards.length) return { minimum: 84, content: 84 };
        const listTop = list.getBoundingClientRect().top;
        const overhead = listTop - panel.getBoundingClientRect().top + 8;
        const cardRects = cards.map((card) => card.getBoundingClientRect());
        const tallest = Math.max(...cardRects.map((rect) => rect.height));
        const bottom = Math.max(...cardRects.map((rect) => rect.bottom - listTop + list.scrollTop));
        return {
          minimum: Math.ceil(overhead + tallest),
          content: Math.ceil(overhead + bottom),
        };
      });
      const minimum = rows.reduce((sum, row) => sum + row.minimum, 0) + Math.max(0, rows.length - 1) * gap;
      // On unusually short screens prefer page scroll over clipping an entire
      // card. Lists themselves scroll when there are additional rows of orders.
      const height = Math.max(available, minimum);
      const boardHeight = `${Math.ceil(height)}px`;
      const rowHeights = allocateStatusRows(rows, height, gap).map((value) => `${value.toFixed(2)}px`).join(' ');
      if (board.style.getPropertyValue('--pedidos-board-height') !== boardHeight) {
        board.style.setProperty('--pedidos-board-height', boardHeight);
      }
      if (stack.style.gridTemplateRows !== rowHeights) stack.style.gridTemplateRows = rowHeights;
    };

    // Measure once before DnD captures dimensions, then freeze until drop or cancel.
    if (!dragging || !wasDraggingRef.current) measure();
    wasDraggingRef.current = dragging;
    if (dragging) return undefined;

    const schedule = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(measure);
    };
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
    if (observer) {
      observer.observe(board.parentElement);
      observer.observe(board);
      board.querySelectorAll('.pedidos-order-card, .pedidos-finished-card').forEach((card) => observer.observe(card));
    }
    window.addEventListener('resize', schedule);
    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('resize', schedule);
    };
  });

  return boardRef;
};
